import { useTranslation } from 'react-i18next'
import type { User } from '../../types'
import { CollapsibleSection } from '../../components/dashboard'
import { formatRole, getStatusIcon } from './helpers'

function TableHeader(): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <thead>
      <tr
        className="border-b"
        style={{ borderColor: 'var(--border)', background: 'var(--table-header)' }}
      >
        <th
          className="px-6 py-3 text-left text-xs font-medium tracking-wider uppercase"
          style={{ color: 'var(--text-muted)' }}
        >
          {t('stats.user')}
        </th>
        <th
          className="px-6 py-3 text-left text-xs font-medium tracking-wider uppercase"
          style={{ color: 'var(--text-muted)' }}
        >
          {t('stats.role')}
        </th>
        <th
          className="px-6 py-3 text-left text-xs font-medium tracking-wider uppercase"
          style={{ color: 'var(--text-muted)' }}
        >
          {t('stats.status')}
        </th>
        <th
          className="px-6 py-3 text-left text-xs font-medium tracking-wider uppercase"
          style={{ color: 'var(--text-muted)' }}
        >
          {t('stats.lastLogin')}
        </th>
      </tr>
    </thead>
  )
}

function UserRow({ user }: { user: User }): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <tr
      className="border-t transition-colors"
      style={{ borderColor: 'var(--border)' }}
      onMouseEnter={(event) => {
        event.currentTarget.style.background = 'var(--hover)'
      }}
      onMouseLeave={(event) => {
        event.currentTarget.style.background = 'transparent'
      }}
    >
      <td className="px-6 py-4 whitespace-nowrap">
        <div>
          <p className="font-medium" style={{ color: 'var(--text)' }}>
            {user.displayName ?? user.username}
          </p>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            {user.email}
          </p>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span
          className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
          style={{ background: 'var(--accent-dim)', color: 'var(--text-dim)' }}
        >
          {formatRole(user.role)}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center gap-2">
          {getStatusIcon(user.status)}
          <span className="text-sm capitalize" style={{ color: 'var(--text-dim)' }}>
            {user.status}
          </span>
        </div>
      </td>
      <td className="px-6 py-4 text-sm whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>
        {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : t('stats.never')}
      </td>
    </tr>
  )
}

export function RecentUsersTable({ users }: { users: User[] }): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <CollapsibleSection id="recent-activity" title={t('stats.recentUsers')}>
      <div
        className="overflow-hidden rounded-xl border"
        style={{ borderColor: 'var(--card-border)', background: 'var(--bg-card)' }}
      >
        <table className="w-full">
          <TableHeader />
          <tbody>
            {users.map((user) => (
              <UserRow key={user.id} user={user} />
            ))}
          </tbody>
        </table>
      </div>
    </CollapsibleSection>
  )
}
